import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Mail, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { formatApiError } from "../lib/formatApiError";
import { FormPageShell } from "../components/FormPageShell";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation("common");
  const [loginMethod, setLoginMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const identifier = loginMethod === "email" ? email : phone;
    if (!identifier || !password) {
      toast.error(t("fillAllFields"));
      return;
    }

    setLoading(true);
    toast.dismiss();
    try {
      const payload = {
        password,
        ...(loginMethod === "email" ? { email } : { phone }),
      };

      const response = await apiClient.post("/auth/login", payload);
      const { access_token, user } = response.data;

      localStorage.setItem("omu_token", access_token);
      setToken(access_token);
      setUser(user);

      toast.success(t("loginSuccess"));

      if (user.role === "admin") {
        navigate("/admin");
      } else if (user.role === "pharmacy") {
        navigate("/dashboard");
      } else if (user.role === "seller") {
        navigate("/seller");
      } else if (user.role === "doctor") {
        navigate("/doctor/prescriptions");
      } else if (user.role === "delivery") {
        navigate("/delivery");
      } else if (user.role === "kam") {
        navigate("/kam");
      } else {
        navigate("/");
      }
    } catch (error) {
      toast.error(formatApiError(error, "AUTH_INVALID_CREDENTIALS"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormPageShell>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="OMU" className="h-20 w-auto mx-auto" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">
            {t("loginTitle")}
          </h1>
          <p className="text-slate-500 mt-2">{t("loginSubtitle")}</p>
        </div>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-6">
            <Tabs value={loginMethod} onValueChange={setLoginMethod} className="mb-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {t("email")}
                </TabsTrigger>
                <TabsTrigger value="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {t("phone")}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              {loginMethod === "email" ? (
                <div className="space-y-2">
                  <Label htmlFor="email">{t("email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl"
                    autoComplete="off"
                    data-testid="login-email-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="phone">{t("phone")}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={t("phonePlaceholder")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-12 rounded-xl"
                    autoComplete="off"
                    data-testid="login-phone-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">{t("password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl pr-12"
                    autoComplete="new-password"
                    data-testid="login-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-omu-blue-500 hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t("forgotPassword")}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("submit")
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <p className="text-slate-500 text-sm">
                {t("noAccount")}{" "}
                <Link
                  to="/register/client"
                  className="text-omu-blue-500 font-medium hover:underline"
                  data-testid="client-register-link"
                >
                  {t("createAccount")}
                </Link>
              </p>
              <p className="text-slate-500 text-sm">
                {t("doctorPrompt")}{" "}
                <Link
                  to="/register/doctor"
                  className="text-omu-blue-500 font-medium hover:underline"
                  data-testid="doctor-register-link"
                >
                  {t("doctorRegister")}
                </Link>
              </p>
              <p className="text-slate-500 text-sm">
                {t("proPrompt")}{" "}
                <Link
                  to="/register"
                  className="text-omu-blue-500 font-medium hover:underline"
                  data-testid="pro-register-link"
                >
                  {t("proRegister")}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-400 mt-6">
          <Link to="/" className="hover:text-slate-600">
            {tc("backHome")}
          </Link>
        </p>
      </div>
    </FormPageShell>
  );
};

export default LoginPage;
